import React, {
	createContext,
	useState,
	useContext,
	ReactNode,
	useEffect,
} from "react";

// Define the shape of the context
interface AlertData {
	message: string;
	type: "success" | "error" | "info" | "warning";
	link?: string;
	linkText?: string;
}

interface AlertContextType {
	alertData: AlertData | null;
	showAlert: (
		message: string,
		type: "success" | "error" | "info" | "warning",
		link?: string,
		linkText?: string,
	) => void;
	clearAlert: () => void;
}

// Create the context
const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Provider component
export const AlertProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [alertData, setAlertData] = useState<AlertData | null>(null);

	const showAlert = (
		message: string,
		type: "success" | "error" | "info" | "warning",
		link?: string,
		linkText?: string,
	) => {
		setAlertData({ message, type, link, linkText });
	};

	const clearAlert = () => {
		setAlertData(null);
	};

	useEffect(() => {
		if (alertData) {
			const timer = setTimeout(() => {
				clearAlert();
			}, 3000); // Auto-close after 3 seconds

			return () => clearTimeout(timer);
		}
	}, [alertData]);

	return (
		<AlertContext.Provider value={{ alertData, showAlert, clearAlert }}>
			{children}
		</AlertContext.Provider>
	);
};

// Custom hook for easier consumption
export const useAlert = (): AlertContextType => {
	const context = useContext(AlertContext);
	if (!context) {
		throw new Error("useAlert must be used within an AlertProvider");
	}
	return context;
};
